import { AlertTriangle, X } from 'lucide-react';
import { identiteEleve, noteVersScore, convertirMaitrise } from '../../utils/collerNotes';

/**
 * Étape 2 : vérifier ET RÉSOUDRE. Trois zones :
 *   - Appariés : élève TanoEd, note collée, pastille /6 ; badge « ≈ » pour
 *     les appariements par similarité, ✕ pour défaire ;
 *   - À résoudre (ambre) : lignes collées inconnues/ambiguës, avec un
 *     sélecteur « Associer à… » (élèves encore libres) ou « Ignorer » ;
 *   - Sans note (info) : élèves TanoEd non couverts → non évalués.
 * Rien n'est bloquant sauf zéro apparié.
 */

// Échelle de maîtrise (codes du suivi, EvalDetail.NIVEAUX non exporté).
const NIVEAUX = {
    6: { code: 'TBM', classes: 'bg-emerald-100 text-emerald-700' },
    5: { code: 'MS', classes: 'bg-green-100 text-green-700' },
    4: { code: 'PM', classes: 'bg-lime-100 text-lime-700' },
    3: { code: 'MF', classes: 'bg-amber-100 text-amber-700' },
    2: { code: 'DM', classes: 'bg-orange-100 text-orange-700' },
    1: { code: 'MI', classes: 'bg-red-100 text-red-700' },
};

const Pastille = ({ score }) => {
    if (score == null) {
        return <span className="inline-flex items-center justify-center rounded-full bg-slate-100 text-slate-400 text-xs font-extrabold px-2.5 py-1">—</span>;
    }
    const n = NIVEAUX[score];
    return (
        <span className={`inline-flex items-center gap-1 rounded-full text-xs font-extrabold px-2.5 py-1 ${n.classes}`}>
            {score}/6 <span className="opacity-70">{n.code}</span>
        </span>
    );
};

const EtapeVerifier = ({
    apparies, aResoudre, elevesSansNote, ignorees, bareme,
    mode = 'note', cases = [], colonnesMaitrise = [], echelles = [],
    onDissocier, onResoudre,
    onRetour, onCreer, envoiEnCours,
}) => {
    const decompose = mode === 'decompose';
    // Score affiché de la maîtrise de la colonne k d'une ligne (échelle
    // déclarée appliquée aux colonnes en chiffres).
    const maitriseConvertie = (ligne, k) => convertirMaitrise(
        (ligne.maitrises || [])[k],
        colonnesMaitrise[k],
        echelles[k]
    );
    // Colonnes IMPORTÉES (celles qui ont un attendu, 2026-09-29) : les seules
    // montrées et comptées ; les autres sont rappelées plus bas.
    const colonnesImportees = decompose ? cases.map((c, k) => c.colIndex ?? k) : [];
    const setImportees = new Set(colonnesImportees);
    const nonImportees = decompose
        ? colonnesMaitrise.filter((_, k) => !setImportees.has(k)).map((c) => c.entete)
        : [];
    const maitrisesImportees = (ligne) => colonnesImportees.map((k) => (ligne.maitrises || [])[k]);
    const notesHorsBareme = decompose
        ? []
        : apparies.filter(({ ligne }) => ligne.note !== null && ligne.note > bareme);
    const nbAvecNote = decompose
        ? apparies.filter(({ ligne }) => maitrisesImportees(ligne).some((s) => s != null)).length
        : apparies.filter(({ ligne }) => ligne.note !== null).length;
    return (
        <div className="space-y-5">
            {/* ── Compteur ── */}
            <p className="text-sm font-bold text-slate-600">
                {nbAvecNote > 0
                    ? `${nbAvecNote} élève${nbAvecNote > 1 ? 's' : ''} recevr${nbAvecNote > 1 ? 'ont' : 'a'} ${decompose ? 'ses maîtrises' : 'sa note'} dans le suivi.`
                    : 'Aucun élève ne recevra de note pour l\'instant.'}
            </p>

            {/* Une note unique dupliquée : le prof doit le savoir AVANT de créer. */}
            {!decompose && cases.length > 1 && (
                <p className="text-sm font-semibold text-blue-800 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5">
                    <strong>Une seule note par élève dans votre collage : elle sera inscrite telle
                    quelle dans les {cases.length} cases cochées.</strong>{' '}
                    Vous pourrez modifier chaque case ensuite, dans l'évaluation.
                </p>
            )}

            {/* Diagnostic global : le collage vient probablement d'un groupe. */}
            {aResoudre.filter((r) => r.raison === 'autreClasse').length > 0 && (
                <p className="text-sm font-semibold text-amber-800 bg-amber-50 border border-amber-300 rounded-xl px-4 py-2.5">
                    <strong>{aResoudre.filter((r) => r.raison === 'autreClasse').length} ligne{aResoudre.filter((r) => r.raison === 'autreClasse').length > 1 ? 's' : ''} porte{aResoudre.filter((r) => r.raison === 'autreClasse').length > 1 ? 'nt' : ''} une autre classe : vérifiez que vous êtes sur la bonne classe.</strong>{' '}
                    S'il s'agit d'un groupe de besoins, lancez « Coller mes notes » depuis ce groupe ;
                    sinon, ignorez ces lignes.
                </p>
            )}

            {/* ── À résoudre ── */}
            {aResoudre.length > 0 && (
                <div className="border-2 border-amber-300 bg-amber-50 rounded-2xl p-4 space-y-3">
                    <p className="flex items-center gap-2 text-sm font-extrabold text-amber-800">
                        <AlertTriangle className="w-4 h-4" />
                        {aResoudre.length} ligne{aResoudre.length > 1 ? 's' : ''} à résoudre (ou à ignorer)
                    </p>
                    {aResoudre.map((item) => (
                        <div key={`ar-${item.ligne.ligneNo}`} className="flex flex-col sm:flex-row sm:items-center gap-2 bg-white rounded-xl px-3 py-2">
                            <div className="flex-1 min-w-0 text-sm">
                                <span className="font-extrabold text-slate-700">« {item.ligne.nomBrut} »</span>
                                <span className="text-slate-400 font-semibold"> · ligne {item.ligne.ligneNo} · </span>
                                <span className="font-bold text-slate-600">
                                    {decompose
                                        ? `${maitrisesImportees(item.ligne).filter((s) => s != null).length}/${colonnesImportees.length} maîtrises`
                                        : (item.ligne.note !== null ? `${item.ligne.noteBrute}/${bareme}` : (item.ligne.special === 'vide' ? 'sans note' : item.ligne.special))}
                                </span>
                                <span className="block text-[12px] font-semibold text-amber-700">
                                    {item.raison === 'homonymes' && 'Plusieurs élèves portent ce nom : choisissez.'}
                                    {item.raison === 'doublon' && `Ce nom est déjà apparié (ligne ${item.ligneEnConflit}).`}
                                    {item.raison === 'inconnu' && 'Aucun élève ne correspond exactement.'}
                                    {item.raison === 'dissocie' && 'Appariement défait : choisissez.'}
                                    {item.raison === 'autreClasse' && `Classe « ${item.ligne.classeBrute} » : cet élève n'est pas de cette classe.`}
                                </span>
                            </div>
                            <select
                                value=""
                                onChange={(e) => onResoudre(item.ligne.ligneNo, e.target.value)}
                                className="border-2 border-amber-200 rounded-xl px-2 py-1.5 text-sm font-semibold bg-white focus:border-amber-400 outline-none"
                            >
                                <option value="" disabled>Associer à…</option>
                                {item.candidats.map((e) => (
                                    <option key={e.id} value={e.id}>{identiteEleve(e)}{e.classe_nom ? ` (${e.classe_nom})` : ''}</option>
                                ))}
                                <option value="ignorer">— Ignorer cette ligne —</option>
                            </select>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Appariés ── */}
            {apparies.length > 0 && (
                <div className="border-2 border-slate-100 rounded-2xl overflow-x-auto">
                    <table className="w-full min-w-[560px] text-sm">
                        <thead>
                            <tr className="bg-slate-50 text-left text-xs font-extrabold text-slate-500 uppercase tracking-wide">
                                <th className="px-4 py-2.5">Élève</th>
                                {decompose ? (
                                    <th className="px-4 py-2.5">Maîtrises (ordre des colonnes collées)</th>
                                ) : (
                                    <>
                                        <th className="px-4 py-2.5">Note collée</th>
                                        <th className="px-4 py-2.5">
                                            Dans le suivi{cases.length > 1 ? ` (${cases.length} cases)` : ''}
                                        </th>
                                    </>
                                )}
                                <th className="px-2 py-2.5" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {apparies.map(({ ligne, eleve, similarite }) => (
                                <tr key={`ap-${ligne.ligneNo}`}>
                                    <td className="px-4 py-2 font-bold text-slate-700">
                                        {identiteEleve(eleve)}
                                        {similarite !== null && (
                                            <span
                                                className="ml-2 text-[11px] font-extrabold text-amber-600 bg-amber-50 rounded-full px-2 py-0.5"
                                                title={`Apparié par ressemblance avec « ${ligne.nomBrut} » (${similarite} %) : vérifiez, ✕ pour défaire.`}
                                            >
                                                ≈ {similarite} %
                                            </span>
                                        )}
                                    </td>
                                    {decompose ? (
                                        <td className="px-4 py-2">
                                            <div className="flex flex-wrap gap-1">
                                                {colonnesImportees.map((k) => (
                                                    <Pastille key={k} score={maitriseConvertie(ligne, k)} />
                                                ))}
                                            </div>
                                        </td>
                                    ) : (
                                        <>
                                            <td className="px-4 py-2 font-semibold text-slate-600">
                                                {ligne.note !== null
                                                    ? `${ligne.noteBrute}/${bareme}`
                                                    : (ligne.special === 'vide' ? 'sans note' : ligne.special)}
                                            </td>
                                            <td className="px-4 py-2">
                                                {/* La recopie MONTRÉE : une pastille par case cochée. */}
                                                {(() => {
                                                    const score = noteVersScore(ligne.note, bareme);
                                                    const n = score == null ? 1 : Math.max(1, cases.length);
                                                    return (
                                                        <div className="flex flex-wrap gap-1">
                                                            {Array.from({ length: n }).map((_, k) => (
                                                                <Pastille key={k} score={score} />
                                                            ))}
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                        </>
                                    )}
                                    <td className="px-2 py-2 text-right">
                                        <button
                                            type="button"
                                            onClick={() => onDissocier(ligne.ligneNo)}
                                            className="w-6 h-6 rounded-md text-slate-300 hover:text-red-500 hover:bg-red-50"
                                            title="Défaire cet appariement"
                                        >
                                            <X className="w-4 h-4 mx-auto" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── Alertes non bloquantes ── */}
            <div className="space-y-3 2xl:space-y-0 2xl:grid 2xl:grid-cols-2 2xl:gap-3">
            {notesHorsBareme.length > 0 && (
                <p className="text-sm font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
                    Note au-dessus du barème (comptée 6/6) :{' '}
                    {notesHorsBareme.map(({ ligne, eleve }) => `${identiteEleve(eleve)} (${ligne.noteBrute})`).join(', ')}.
                </p>
            )}
            {ignorees.length > 0 && (
                <p className="text-sm font-semibold text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5">
                    Lignes du collage non lues : {ignorees.map((i) => `ligne ${i.ligneNo} (${i.raison})`).join(', ')}.
                </p>
            )}
            {nonImportees.length > 0 && (
                <p className="text-sm font-semibold text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5">
                    {nonImportees.length > 1 ? 'Colonnes non importées' : 'Colonne non importée'} (sans attendu) :{' '}
                    {nonImportees.map((e) => `« ${e} »`).join(', ')}.
                </p>
            )}
            {elevesSansNote.length > 0 && (
                <p className="text-sm font-semibold text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5">
                    {decompose ? 'Absents du collage' : 'Sans note dans le collage'} (resteront « non évalués ») :{' '}
                    {elevesSansNote.map((e) => identiteEleve(e)).join(', ')}.
                </p>
            )}
            </div>

            {/* ── Actions ── */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:items-center sm:justify-between">
                <button
                    type="button"
                    onClick={onRetour}
                    disabled={envoiEnCours}
                    className="text-sm font-extrabold text-slate-500 hover:text-slate-700 disabled:opacity-50"
                >
                    ← Retour
                </button>
                <button
                    type="button"
                    onClick={onCreer}
                    disabled={apparies.length === 0 || envoiEnCours}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-extrabold px-6 py-2.5 rounded-xl transition-colors"
                >
                    {envoiEnCours ? 'Création en cours…' : 'Créer l\'évaluation'}
                </button>
            </div>
        </div>
    );
};

export default EtapeVerifier;
